import { useEffect, useState } from 'react';
import {
  DEFAULT_PASSWORD_RULES,
  PasswordRule,
  getPasswordRules,
} from '../../../api-client/get-password-rules';

// Fetches the Connect password policy once. The rules are language-agnostic (each carries a Maia
// CMS key the caller localises via i18n), so there is no per-language fetch. Seeds with the built-in
// DEFAULT_PASSWORD_RULES so the password step validates and renders its checklist from the first
// render — including before the fetch resolves and if it fails — then swaps in the authoritative
// rules once they arrive. The Java sign-up endpoint re-validates on submit regardless.
export function usePasswordRules(): PasswordRule[] {
  const [rules, setRules] = useState<PasswordRule[]>(DEFAULT_PASSWORD_RULES);

  useEffect(() => {
    let active = true;
    getPasswordRules().then(response => {
      if (active) {
        setRules(response.passwordRules);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  return rules;
}

export function matchesRule(rule: PasswordRule, password: string): boolean {
  try {
    return new RegExp(rule.pattern).test(password);
  } catch {
    // A malformed pattern must not wedge the step; treat it as satisfied and let the server decide.
    return true;
  }
}

// The length bound encoded in a `^.{min,max}$` pattern, for interpolating localised copy (e.g.
// "at least {{length}} characters"). Returns the upper bound when present (max-length rules),
// otherwise the lower bound (min-length rules), or undefined for rules without a length.
export function lengthFromPattern(pattern: string): number | undefined {
  // Handles `{n}`, `{n,}` (min-only, e.g. the min-length rule) and `{n,m}` (max-length rule).
  const match = pattern.match(/\{(\d+)(?:,(\d*))?\}/);
  if (!match) {
    return undefined;
  }
  return match[2] ? Number(match[2]) : Number(match[1]);
}
